import type React from "react";
import {useNavigate} from "react-router";
import {Alert, Skeleton, Stack, Tab} from "@mui/material";
import {CustomTabs} from "~/components/tabs/Tabs";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2TabKey} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {PtV2EmptyState} from "~/experiments/pages/payment-terms-v2/components/PtV2EmptyState.tsx";

type Props = {
    tab: PtV2TabKey;
    kpis?: SitKpiPill[];
    /** Muted text beside the KPI pills, e.g. the data period. */
    kpiAside?: React.ReactNode;
    /** The tab's one-line summary. */
    summary?: React.ReactNode;
    loading?: boolean;
    error?: boolean;
    /** Shown in place of the empty state on a tab that is useful before an import (Data sources). */
    beforeImport?: React.ReactNode;
    children?: React.ReactNode;
};

/** Order fixed by design: the tab's KPI pills, the tab bar, the summary line, then the content. */
export const PtV2TabFrame: React.FC<Props> = ({tab, kpis, kpiAside, summary, loading, error, beforeImport, children}) => {
    const {hasData, summaryLoading, summaryError, tabPath, tabs} = usePtV2Context();
    const navigate = useNavigate();
    const showData = hasData && !summaryError;
    // KPIs and summary read the tab's own data too, so they wait for it rather than flash dashes.
    const ready = showData && !summaryLoading && !loading && !error;

    let body: React.ReactNode;
    if (summaryLoading || (showData && loading)) {
        body = <div className="ptv2-section">
            <Skeleton variant="rounded" height={120}/>
            <Skeleton variant="rounded" height={260}/>
        </div>;
    } else if (summaryError || (showData && error)) {
        body = <Alert severity="error" sx={{mt: 2}}>
            The payment terms data could not be loaded. Reload the page to try again.
        </Alert>;
    } else if (!hasData) {
        body = beforeImport ?? <PtV2EmptyState/>;
    } else {
        body = children;
    }

    return <>
        {ready && kpis && <Stack direction="row" alignItems="center" columnGap={2}
                                 sx={{'& > .sit-kpis': {flex: '1 1 auto', minWidth: 0}}}>
            <SitKpiHeader items={kpis}/>
            {kpiAside && <Stack component="span" className="ptv2-muted ptv2-num" sx={{flexShrink: 0, fontSize: 13}}>
                {kpiAside}
            </Stack>}
        </Stack>}
        <CustomTabs
            className="ptv2-tabs"
            value={tab}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(_e, v: PtV2TabKey) => navigate(tabPath(v))}>
            {tabs.map(t => <Tab key={t.key} value={t.key}
                                label={t.hidden ? `${t.label} · hidden` : t.label}
                                title={t.hidden ? 'Hidden from customers (Payment Terms settings)' : undefined}/>)}
        </CustomTabs>
        {ready && summary && <p className="ptv2-summary">{summary}</p>}
        {body}
    </>;
};
